import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Progress from './Progress';
import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';
import './styles/VideoEditor.css';
import jschardet from 'jschardet';
import iconv from 'iconv-lite';
import { languages } from './LanguageSelector';
import LanguageSelector from './LanguageSelector';
import PlanDetailsPanel from './PlanDetailsPanel';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from './dictionary/translateVideoEditor';
import { adTranslations } from './dictionary/translateAdSimulator';

function VideoEditor({ onLogout }) {
  const [transitionRatio, setTransitionRatio] = useState(0.7);
  const [videoFile, setVideoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [subtitleFile, setSubtitleFile] = useState(null);
  const [cutSubtitleFile, setCutSubtitleFile] = useState(null);
  const [allGeneratedFiles, setAllGeneratedFiles] = useState([]);
  const [, setVideoURL] = useState('');
  const [mediaURL, setMediaURL] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [dialogue, setDialogue] = useState('');
  const [dubbing, setDubbing] = useState(false);
  const [syncVoiceClone, setSyncVoiceClone] = useState(false);
  const [syncLipMovement, setSyncLipMovement] = useState(false);
  const [syncLipEnhance, setSyncLipEnhance] = useState(false);
  const [inputLanguage, setInputLanguage] = useState('auto');
  const [transcribe, setTranscribe] = useState(true);
  const [embed, setEmbed] = useState(true);
  const [joinVideo, setJoinVideo] = useState(false);
  const [cutVideo, setCutVideo] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [cutProgress, setCutProgress] = useState(0);
  const [cutSuccess, setCutSuccess] = useState(false);
  const [joinProgress, setJoinProgress] = useState(0);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState('');

  const [processedVideos, setProcessedVideos] = useState([]);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const isRecordingRef = useRef(false);
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // -------------------------------
  // Dados de Plano / Usuário
  // -------------------------------
  const { selectedLanguage, user } = location.state || {};
  const [planDetails, setPlanDetails] = useState(user?.planDetails || {});
  const [usedStorage, setUsedStorage] = useState(user?.usedStorage || 0);
  const [dailyUploads, setDailyUploads] = useState(user?.dailyUploads || 0);
  const [outputLanguage, setOutputLanguage] = useState(selectedLanguage);
  const [translatePage, setTranslatePage] = useState(false);
  const storedUser = JSON.parse(sessionStorage.getItem('user'));
  const username = storedUser?.email?.split('@')[0] || 'usuário';
  const [storedPassword, setStoredPassword] = useState(user?.password || '');

  // -------------------------------
  // Ads: showAd, simulação/produção
  // -------------------------------
  const [showAd, setShowAd] = useState(false);
  const isSimulation = process.env.REACT_APP_AD_SIMULATION === 'true';
  const [feedback, setFeedback] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);


  // -------------------------------
  // Efeito para recarregar ads
  // -------------------------------
  useEffect(() => {
    if (showAd && !isSimulation) {
      // Carrega o anúncio do AdSense
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    }
  }, [showAd, isSimulation]);

  // -------------------------------
  // Função que exibe anúncio e, ao terminar, executa callback
  // -------------------------------
  const showAdAndThen = (callback) => {
    setShowAd(true);

    if (isSimulation) {
      // Modo simulado: 5s
      setTimeout(() => {
        setShowAd(false);
        if (typeof callback === 'function') {
          callback();
        }
      }, 5000);
    } else {
      // Produção: checa carregamento, depois 5s
      const checkAdLoaded = () => {
        const adElement = document.querySelector('.adsbygoogle');
        if (adElement && adElement.offsetHeight > 0) {
          // Espera 5s para o usuário ver
          setTimeout(() => {
            setShowAd(false);
            if (typeof callback === 'function') {
              callback();
            }
          }, 5000);
        } else {
          setTimeout(checkAdLoaded, 500);
        }
      };
      checkAdLoaded();
    }
  };

  // -------------------------------
  // Exibir anúncio ao carregar página se Vision Free
  // -------------------------------
  useEffect(() => {
    if (planDetails?.planType === 'Vision Free') {
      showAdAndThen(() => {
        // Após carregar anúncio ao entrar
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDetails]);

  // -------------------------------
  // Restrições de flags por plano
  // -------------------------------
  const isVisionaryFree = planDetails?.planType === 'Vision Free';
  const isVisionaryStarter = planDetails?.planType === 'Vision Starter';
  const isVisionaryGrowth = planDetails?.planType === 'Vision Growth';
  //const isVisionaryPremium = planDetails?.planType === 'Vision Premium';

  // Define desativação de flags principais
  const disableDubbing = false; ////isVisionaryFree || isVisionaryStarter; // Exemplo
  const disableJoinVideo = isVisionaryFree; // Free não pode Join
  const disableCutVideo = isVisionaryFree;  // Free não pode Cut

  // Define desativação das sub-flags de dubbing
  const disableSubFlagCloneVoice = isVisionaryFree;
  const disableSubFlagLipMovement = isVisionaryFree || isVisionaryStarter;
  const disableSubFlagLipEnhance = isVisionaryFree || isVisionaryStarter || isVisionaryGrowth;

  // -------------------------------
  // Tradutor de texto
  // -------------------------------
  const t = React.useCallback(
    (key) => {
      const activeLang = translatePage ? outputLanguage : selectedLanguage;
      return translations[activeLang]?.[key] || key;
    },
    [translatePage, outputLanguage, selectedLanguage]
  );

  const s = React.useCallback(
    (key) => adTranslations[selectedLanguage]?.[key] || key,
    [selectedLanguage]
  );

  // -------------------------------
  // Pegar planDetails do sessionStorage
  // -------------------------------
  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem('user'));
    if (storedUser) {
      setPlanDetails(storedUser.planDetails || {});
      setUsedStorage(storedUser.usedStorage || 0);
      setDailyUploads(storedUser.dailyUploads || 0);
      setStoredPassword(storedUser.password || '');
    }
  }, []);

  // -------------------------------
  // Fetch Updated Plan
  // -------------------------------
  const fetchUpdatedPlanDetails = async () => {
    try {
      const storedUser = JSON.parse(sessionStorage.getItem('user'));
      if (!storedUser || !storedPassword) {
        console.error('Erro: usuário ou senha não encontrados.');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/users/auth`,
        {
          email: storedUser.email,
          password: storedPassword,
        }
      );

      const { user } = response.data;
      setPlanDetails(user.planDetails);
      setUsedStorage(user.usedStorage);
      setDailyUploads(user.dailyUploads);

      // Atualiza no sessionStorage
      sessionStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('Erro ao buscar detalhes do plano:', error);
    }
  };

  // -------------------------------
  // Cleanup antes de sair (logout/unload)
  // -------------------------------
  const handleCleanup = useCallback(async () => {
    try {
      // Enviamos ao backend somente os arquivos que foram gerados localmente
      await fetch(`${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/cleanup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filesToDelete: allGeneratedFiles }),
      });
      console.log(t('cleanupSuccess'));
    } catch (error) {
      console.error(t('cleanupError'), error);
    }
  }, [t, allGeneratedFiles]);

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;
  
    try {
      await axios.post(`${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/users/feedback`, {
        email: storedUser?.email || '',
        username: username,
        message: feedback,
      });
  
      setFeedback('');
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
    }
  };
  
  const generateAIVideoRunway = async () => {
    if (!dialogue.trim()) return;
  
    setIsInteracting(true);
    setUploadProgress(0);
    setUploadSuccess(false);
    setError('');
  
    try {
      const formData = new FormData();
      formData.append('promptText', dialogue);
      formData.append('promptImage', imageFile);
      formData.append('transitionRatio', transitionRatio);
      formData.append('join', joinVideo);
      formData.append('model', 'gen3a_turbo');
      formData.append('aspectRatio', '16:9');
      formData.append('frameRate', 24);
      formData.append('guidanceScale', 12);
      formData.append('motion', 1);
      formData.append('negativePrompt', '');
      formData.append('seed', '');
  
      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/generate-ai-video-runway`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            email: storedUser?.email || ''
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        }
      );
  
      if (response.data?.videoUrls) {
        setProcessedVideos(prev => [...prev, ...response.data.videoUrls.map(url => ({
          id: uuidv4(),
          url
        }))]);
  
        if (response.data.generatedFiles) {
          setAllGeneratedFiles(prev => [...prev, ...response.data.generatedFiles]);
        }
  
        if (response.data.zipPath) {
          const zipUrl = `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/${response.data.zipPath}`;
          const link = document.createElement('a');
          link.href = zipUrl;
          link.download = `ai_generated_videos.zip`;
          link.click();
        }
  
        setUploadSuccess(true);
      }
    } catch (error) {
      console.error('Erro ao gerar vídeo IA:', error);
      setError('Erro ao gerar vídeo IA.');
    } finally {
      setIsInteracting(false);
      setUploadProgress(0);
    }
  };  
  
  // const generateAIVideoPika = async () => {
  //   if (!feedback.trim()) return;
  //   try {
  //     // Exemplo de body Pika
  //     const body = {
  //       promptText: feedback,
  //       model: "1.5", // ou "2.0", "Turbo"
  //       // se estiver usando Turbo:
  //       // video: "https://exemplo.com/base.mp4",
  //       // image: "https://exemplo.com/img.png",
  //       aspectRatio: "16:9",
  //       frameRate: 24,
  //       guidanceScale: 12,
  //       motion: 1,
  //       negativePrompt: "",
  //       seed: null,
  //     };

  //     const response = await axios.post(
  //       `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/generate-ai-video-pika`,
  //       body
  //     );

  //     console.log("Resposta do backend:", response.data);
  //     if (response.data.videoUrls) {
  //       const newVideos = response.data.videoUrls.map((url) => ({
  //         id: uuidv4(),
  //         url
  //       }));
  //       setProcessedVideos((prev) => [...prev, ...newVideos]);
  //     }
  //   } catch (error) {
  //     console.error("Erro ao gerar vídeo IA:", error);
  //   }
  // };


  // -------------------------------
  // Logout (com anúncio se for Free ou Growth)
  // -------------------------------
  const handleLogout = async () => {
    setIsInteracting(true);
    try {
      await handleCleanup();
      onLogout();
      sessionStorage.clear();
      setStoredPassword('');
      navigate('/');
    } catch (error) {
      console.error(t('logoutError'), error);
    } finally {
      setIsInteracting(false);
    }
  };

  const handleLogoutClick = () => {
    if (isVisionaryFree || isVisionaryGrowth) {
      showAdAndThen(() => {
        handleLogout();
      });
    } else {
      handleLogout();
    }
  };

  // -------------------------------
  // Join Videos
  // -------------------------------
  const handleJoinVideos = useCallback(async () => {
    setIsInteracting(true);
    setUploadSuccess(false);
    setJoinSuccess(false);
    setError(null);

    const videoUrls = processedVideos.map((video) => video.url);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/join`,
        { videoUrls },
        {
          headers: { 'Content-Type': 'application/json' },
          responseType: 'json',
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setJoinProgress(percentCompleted);
          },
        }
      );

      if (response.data && response.data.joinedVideoUrl) {
        const joinedVideoUrl = response.data.joinedVideoUrl;
        setProcessedVideos([{ id: uuidv4(), url: joinedVideoUrl }]);
        setError('');
        setJoinProgress(0);
        setJoinSuccess(true);
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (error) {
      console.error(t('errorJoinVideos'), error);
      setError(t('errorJoinVideos'));
      setJoinProgress(0);
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } finally {
      setIsInteracting(false);
    }
  }, [processedVideos, t]);

  const handleJoinVideosClick = () => {
    if (isVisionaryFree || isVisionaryStarter) {
      showAdAndThen(() => {
        handleJoinVideos();
      });
    } else {
      handleJoinVideos();
    }
  };

  // -------------------------------
  // Efeito: se a aba for fechada
  // -------------------------------
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isInteracting) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const handleUnload = async () => {
      if (!isInteracting) {
        await handleCleanup();
        sessionStorage.clear();
        navigate('/');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [isInteracting, handleCleanup, navigate]);

  // -------------------------------
  // Carregar meta-dados do vídeo
  // -------------------------------
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.onloadedmetadata = () => {
        setDuration(videoRef.current.duration);
        setEndTime(videoRef.current.duration);
      };
    }
  }, [mediaURL]);

  // -------------------------------
  // Ajustar transitionRatio conforme tipo de mídia
  // -------------------------------
  useEffect(() => {
    if (mediaType === 'image') {
      setTransitionRatio(9.9);
    } else if (mediaType === 'video') {
      setTransitionRatio(0.7);
    }
  }, [mediaType]);

  // -------------------------------
  // Tocar música de fundo
  // -------------------------------
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.error(t('audioError'), error);
      });
    }
  }, [t]);

  // -------------------------------
  // Se joinVideo = true e vídeos > 1
  // -------------------------------
  useEffect(() => {
    if (uploadSuccess && joinVideo && processedVideos.length > 1) {
      handleJoinVideos();
    }
  }, [uploadSuccess, joinVideo, processedVideos, handleJoinVideos]);

  // -------------------------------
  // Ajuste cirúrgico: transcribe FALSE => define outputLanguage?
  // -------------------------------
  useEffect(() => {
    if (!transcribe) {
      if (dialogue) {
        setOutputLanguage('auto');
      } else {
        setOutputLanguage(selectedLanguage);
      }
    }
  }, [dialogue, selectedLanguage, transcribe]);

  const handleMediaChange = (event) => {
    setIsInteracting(true);
    const file = event.target.files[0];
    if (!file) {
      setIsInteracting(false);
      return;
    }
  
    const url = URL.createObjectURL(file);
  
    if (file.type.startsWith('image/')) {
      setMediaType('image');
      setImageFile(file);
      setVideoFile(null);
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
      setVideoFile(file);
      setImageFile(null);
    } else {
      setError('Formato não suportado.');
      setIsInteracting(false);
      return;
    }
  
    setMediaURL(url);
    setCutVideo(false);
    setIsInteracting(false);
  };  

  // -------------------------------
  // Input de legenda
  // -------------------------------
  const handleSubtitleChange = (event) => {
    setIsInteracting(true);
    const file = event.target.files[0];
    if (!file) {
      setIsInteracting(false);
      return;
    }
    setSubtitleFile(file);
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      if (tracks.length > 0) {
        tracks[0].mode = 'disabled';
      }
      detectAndSetSubtitleLanguage(file);
    }
    setIsInteracting(false);
  };

  // Detectar idioma da legenda e inserir track
  const detectAndSetSubtitleLanguage = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);
      const detected = jschardet.detect(buffer);
      let encoding = detected.encoding;

      if (!iconv.encodingExists(encoding)) {
        console.warn(
          `${t('encodingNotSupported')} ${encoding}, ${t('fallbackUtf8')}`
        );
        encoding = 'utf-8';
      }
      const text = iconv.decode(buffer, encoding);

      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/detectLanguage`,
        { dialogue: text }
      );
      const { label, srclang } = response.data;

      const track = document.createElement('track');
      track.kind = 'subtitles';
      track.label = label;
      track.srclang = srclang;
      track.src = URL.createObjectURL(file);
      track.default = true;

      videoRef.current.appendChild(track);
    } catch (error) {
      console.error(t('errorDetectSubtitleLanguage'), error);
    }
  };

  // -------------------------------
  // Input de texto (dialogue)
  // -------------------------------
  const handleDialogueChange = (event) => {
    setDialogue(event.target.value);
  };

  const handleTransitionRatioChange = (value) => {
    let newValue = value.replace(',', '.');
    let floatVal = parseFloat(newValue);
    if (isNaN(floatVal)) {
      floatVal = 0.7;
    }
    if (floatVal > 9.9) floatVal = 9.9;
    if (floatVal < 0.1) floatVal = 0.1;
    floatVal = Math.round(floatVal * 100) / 100;
    setTransitionRatio(floatVal);
  };

  // -------------------------------
  // Toggles
  // -------------------------------
  const toggleDubbing = () => {
    setIsInteracting(true);
    if (dubbing) {
      setSyncVoiceClone(false);
      setSyncLipMovement(false);
      setSyncLipEnhance(false);
    }
    setDubbing(!dubbing);
    setIsInteracting(false);
  };

  const toggleSyncVoiceClone = () => {
    setIsInteracting(true);
    setSyncVoiceClone(!syncVoiceClone);
    setIsInteracting(false);
  };

  const toggleSyncLipMovement = () => {
    setIsInteracting(true);
    setSyncLipMovement(!syncLipMovement);
    setIsInteracting(false);
  };

  const toggleSyncLipEnhance = () => {
    setIsInteracting(true);
    setSyncLipEnhance(!syncLipEnhance);
    setIsInteracting(false);
  };

  const toggleTranscribe = () => {
    setIsInteracting(true);
    setTranscribe(!transcribe);
    setIsInteracting(false);
  };

  const toggleEmbed = () => {
    setIsInteracting(true);
    setEmbed(!embed);
    setIsInteracting(false);
  };

  const toggleCutVideo = () => {
    setIsInteracting(true);
    setCutVideo(!cutVideo);
    setIsInteracting(false);
  };

  const toggleJoinVideo = () => {
    setIsInteracting(true);
    setJoinVideo(!joinVideo);
    setIsInteracting(false);
  };

  // -------------------------------
  // Reset sliders
  // -------------------------------
  const resetSliders = () => {
    setStartTime(0);
    setEndTime(duration);
  };

  // -------------------------------
  // Correção de legendas
  // -------------------------------
  const correctSubtitles = async (segments) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/decode`,
        { segments }
      );
      return response.data.correctedSegments;
    } catch (error) {
      console.error(t('errorCorrectSubtitles'), error);
      throw error;
    }
  };

  // -------------------------------
  // Corte de legenda
  // -------------------------------
  const cutSubtitle = async (startTime, endTime, subtitleFile) => {
    try {
      const arrayBuffer = await subtitleFile.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);
      const detected = jschardet.detect(buffer);
      let encoding = detected.encoding;
      if (!iconv.encodingExists(encoding)) {
        console.warn(
          `${t('encodingNotSupported')} ${encoding}, ${t('fallbackUtf8')}`
        );
        encoding = 'utf-8';
      }
      const text = iconv.decode(buffer, encoding);

      const srtEntries = text.split(/\n\s*\n/).map((entry) => {
        const [index, time, ...textLines] = entry.split('\n');
        const [start, end] = time.split(' --> ').map((ti) => {
          const [hours, minutes, seconds] = ti.split(':');
          return (
            +hours * 3600 + +minutes * 60 + +seconds.replace(',', '.')
          ) * 1000;
        });
        return { index, start, end, text: textLines.join('\n') };
      });

      let cutSubtitles = srtEntries
        .filter(
          (entry) => entry.start >= startTime * 1000 && entry.end <= endTime * 1000
        )
        .map((entry) => {
          entry.start -= startTime * 1000;
          entry.end -= startTime * 1000;
          return entry;
        });

      const cutSubtitleTexts = cutSubtitles.map((entry) => entry.text);
      const correctedTexts = await correctSubtitles(cutSubtitleTexts);

      cutSubtitles = cutSubtitles.map((entry, index) => {
        entry.text = correctedTexts[index];
        return entry;
      });

      const cutSubtitleText = cutSubtitles
        .map((entry) => {
          const start = new Date(entry.start).toISOString().substr(11, 12).replace('.', ',');
          const end = new Date(entry.end).toISOString().substr(11, 12).replace('.', ',');
          return `${entry.index}\n${start} --> ${end}\n${entry.text}`;
        })
        .join('\n\n');

      const cutSubtitleBlob = new Blob([cutSubtitleText], { type: 'text/plain' });
      return new File([cutSubtitleBlob], 'cut_subtitle.srt', { type: 'text/plain' });
    } catch (error) {
      console.error(t('errorCutSubtitle'), error);
      throw error;
    }
  };

  // -------------------------------
  // Ação real de cortar vídeo
  // -------------------------------
  const handleCutVideo = () => {
    setIsInteracting(true);
    setCutSuccess(false);
    const videoElement = videoRef.current;

    if (isRecordingRef.current) {
      console.error(t('errorRecordingInProgress'));
      setIsInteracting(false);
      return;
    }

    const mediaStream = videoElement.captureStream();
    const mediaRecorder = new MediaRecorder(mediaStream);
    mediaRecorderRef.current = mediaRecorder;
    let chunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const cutBlob = new Blob(chunks, { type: 'video/webm' });
      const cutVideoURL = URL.createObjectURL(cutBlob);
      setVideoURL(cutVideoURL);
      const cutFile = new File([cutBlob], 'cut_video.webm', {
        type: 'video/webm',
      });
      setVideoFile(cutFile);
      setCutProgress(0);
      setCutSuccess(true);
      resetSliders();
      setCutVideo(false);
      isRecordingRef.current = false;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      if (subtitleFile) {
        try {
          const newCutSubtitleFile = await cutSubtitle(
            startTime,
            endTime,
            subtitleFile
          );
          if (newCutSubtitleFile) {
            setCutSubtitleFile(newCutSubtitleFile);
            const tracks = videoRef.current.textTracks;
            if (tracks.length > 0) {
              tracks[0].mode = 'disabled';
            }
            detectAndSetSubtitleLanguage(newCutSubtitleFile);
          }
        } catch (error) {
          console.error(t('errorCutSubtitle'), error);
        }
      }
    };

    const startRecording = () => {
      if (mediaRecorder.state === 'recording') {
        console.error(t('errorRecordingInProgress'));
        return;
      }

      if (mediaStream.getTracks().length > 0) {
        mediaRecorder.start();
        isRecordingRef.current = true;
        videoElement.play();
        let elapsedTime = 0;
        const interval = setInterval(() => {
          elapsedTime += 1;
          const progress = (elapsedTime / (endTime - startTime)) * 100;
          setCutProgress(Math.min(progress, 100));
          if (elapsedTime >= endTime - startTime) {
            clearInterval(interval);
          }
        }, 1000);

        setTimeout(() => {
          mediaRecorder.stop();
          videoElement.pause();
        }, (endTime - startTime) * 1000);
      } else {
        console.error(t('errorNoMediaTracks'));
      }
    };

    videoElement.currentTime = startTime;
    videoElement.onseeked = startRecording;
    setIsInteracting(false);
  };

  const handleCutVideoClick = () => {
    if (isVisionaryFree || isVisionaryStarter) {
      showAdAndThen(() => {
        handleCutVideo();
      });
    } else {
      handleCutVideo();
    }
  };

  // -------------------------------
  // Ação de processar vídeo
  // -------------------------------
  const uploadAndCut = async () => {
    setIsInteracting(true);
    setUploadSuccess(false);
    setError('');

    if (!videoFile) {
      setError(t('uploadVideoError'));
      setIsInteracting(false);
      return;
    }

    // Verifica tamanho
    const fileSizeMB = (videoFile.size / (1024 * 1024)).toFixed(2);
    const maxFileSizeMB = parseFloat(
      planDetails?.limits?.maxFileSize?.replace('GB', '')
    ) * 1024;

    if (fileSizeMB > maxFileSizeMB) {
      setError(
        `O tamanho do arquivo (${fileSizeMB} MB) excede o limite de ${maxFileSizeMB} MB.`
      );
      setIsInteracting(false);
      return;
    }

    // Verifica limite diário
    if (planDetails?.limits?.dailyLimit <= processedVideos.length) {
      setError(
        `Você atingiu o limite diário de uploads: ${planDetails.limits.dailyLimit}`
      );
      setIsInteracting(false);
      return;
    }

    // Verifica se tem alguma opção marcante
    if (!transcribe && !dubbing && !subtitleFile && !cutSubtitleFile) {
      setError(t('uploadSubtitleError'));
      setIsInteracting(false);
      return;
    }

    const formData = new FormData();
    formData.append('video', videoFile);

    if (!transcribe && !dubbing && (subtitleFile || cutSubtitleFile)) {
      formData.append('subtitle', cutSubtitleFile || subtitleFile);
    }

    formData.append('dialogue', dialogue);
    formData.append('inputLanguage', inputLanguage);
    formData.append('outputLanguage', outputLanguage);
    formData.append('source', 'external');
    formData.append('syncVoiceClone', syncVoiceClone);
    formData.append('syncLipMovement', syncLipMovement);
    formData.append('syncLipEnhance', syncLipEnhance);
    formData.append('dubbing', dubbing);
    formData.append('transcribe', transcribe);
    formData.append('embed', embed);
    formData.append('joinVideo', joinVideo);
    formData.append('transitionRatio', transitionRatio);

    const url = !transcribe
      ? `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/subtitle`
      : `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/dubbing`;

    try {
      const storedUser = JSON.parse(sessionStorage.getItem('user'));

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          email: storedUser?.email || ''
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
        responseType: 'json',
      });

      if (response.data && response.data.videoUrls) {
        const videosWithId = response.data.videoUrls.map((videoUrl) => ({
          id: uuidv4(),
          url: videoUrl,
        }));
        setProcessedVideos((prev) => [...prev, ...videosWithId]);

        // Se o backend retornou "generatedFiles"
        if (response.data.generatedFiles) {
          // Guardamos cumulativamente
          setAllGeneratedFiles((prev) => [...prev, ...response.data.generatedFiles]);
        }

        // Download do ZIP (se existir)
        if (response.data.zipPath) {
          const zipUrl = `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/${response.data.zipPath}`;
          const link = document.createElement('a');
          link.href = zipUrl;
          link.setAttribute(
            'download',
            `${dialogue.replace(/\s+/g, '_')}_files.zip`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setUploadProgress(0);
        setUploadSuccess(true);
        audioRef.current.pause();
        audioRef.current.currentTime = 0;

        // Atualiza detalhes do plano
        await fetchUpdatedPlanDetails();

        // Se for join
        if (response.data.joinVideo && response.data.videoUrls.length > 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          handleJoinVideos();
        }
      }
    } catch (error) {
      console.error(t('uploadError'), error);

      if (error.response) {
        const errorMessage = error.response.data?.error || '';

        switch (error.response.status) {
          case 400:
            setError(errorMessage || t('invalidRequest'));
            break;
          case 404:
            setError(errorMessage || t('userNotFound'));
            break;
          case 413:
            setError(errorMessage || t('insufficientStorage'));
            break;
          case 429:
            setError(errorMessage || t('dailyLimitExceeded'));
            break;
          case 500:
            setError(
              t('unexpectedError').replace('{message}', errorMessage) ||
                t('internalServerError')
            );
            break;
          default:
            setError(
              `${t('unexpectedError').replace(
                '{message}',
                error.response.status
              )}. ${errorMessage}`
            );
            break;
        }
      } else if (error.request) {
        setError(t('connectionError'));
      } else {
        setError(
          `${t('unexpectedError').replace('{message}', error.message)}`
        );
      }

      setUploadProgress(0);
      setUploadSuccess(false);
      audioRef.current.pause();
    } finally {
      setIsInteracting(false);
    }
  };

  const handleProcessVideoClick = () => {
    if (mediaType === 'image' && imageFile) {
      if (isVisionaryFree || isVisionaryGrowth) {
        showAdAndThen(() => {
          generateAIVideoRunway();
        });
      } else {
        generateAIVideoRunway();
      }
    } else if (mediaType === 'video' && videoFile) {
      if (isVisionaryFree || isVisionaryGrowth) {
        showAdAndThen(() => {
          uploadAndCut();
        });
      } else {
        uploadAndCut();
      }
    } else {
      setError('Nenhum arquivo selecionado. Por favor, selecione uma imagem ou vídeo.');
    }
  };  

  // -------------------------------
  // Deletar vídeo processado
  // -------------------------------
  const handleDelete = (id) => {
    setIsInteracting(true);
    setProcessedVideos((prev) => prev.filter((video) => video.id !== id));
    setIsInteracting(false);
  };

  // -------------------------------
  // Drag & Drop de vídeos
  // -------------------------------
  const handleDragStart = (event, index) => {
    setIsInteracting(true);
    event.dataTransfer.setData('text/plain', index);
    setIsInteracting(false);
  };

  const handleDrop = (event, index) => {
    setIsInteracting(true);
    const draggedIndex = event.dataTransfer.getData('text');
    if (draggedIndex === '') {
      setIsInteracting(false);
      return;
    }
    const newProcessedVideos = [...processedVideos];
    const [draggedVideo] = newProcessedVideos.splice(draggedIndex, 1);
    newProcessedVideos.splice(index, 0, draggedVideo);
    setProcessedVideos(newProcessedVideos);
    setIsInteracting(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // -------------------------------
  // Download
  // -------------------------------
  const downloadFile = (file, fileName) => {
    setIsInteracting(true);
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsInteracting(false);
  };

  // -------------------------------
  // Áudio: mute/unmute
  // -------------------------------
  const toggleMute = () => {
    setIsInteracting(true);
    setIsMuted(!isMuted);
    if (audioRef.current) {
      if (!isMuted) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current.play().catch((error) => {
          console.error(t('audioError'), error);
        });
      }
    }
    setIsInteracting(false);
  };

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <div className="video-editor">
      {/* Mensagem de boas-vindas */}
      <div className="welcome-message">
        <h2>{t('welcomeMessage').replace('{username}', username)}</h2>
        <p>{t('tagline')}</p>
      </div>

      {/* Painel de detalhes do plano */}
      <div className="plan-details-wrapper">
        <PlanDetailsPanel
          planDetails={planDetails}
          usedStorage={usedStorage}
          dailyUploads={dailyUploads}
          selectedLanguage={selectedLanguage}
          outputLanguage={outputLanguage}
          translatePage={translatePage}
        />

        <button
          className="custom-button upgrade-plan-button"
          onClick={() =>
            navigate('/plan-selection', {
              state: { user: storedUser, selectedLanguage: selectedLanguage || 'en' },
            })
          }
          style={{ marginTop: '20px' }}
        >
          {t('upgradePlan')}
        </button>

        {/* Seção de Feedback */}
        <div className="feedback-section">
            <h3>{t('feedbackTitle')}</h3>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('feedbackPlaceholder')}
              className="feedback-textarea"
            />
            <button 
              className="custom-button feedback-button" 
              onClick={handleFeedbackSubmit}
              disabled={feedbackSubmitted}
            >
              {feedbackSubmitted ? t('feedbackSent') : t('sendFeedback')}
            </button>
          </div>
      </div>

      {/* Botão de mute/unmute audio de fundo */}
      <div
        onClick={toggleMute}
        style={{ cursor: 'pointer', marginBottom: '10px' }}
      >
        <i
          className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}
          style={{ fontSize: '24px', color: 'white' }}
        ></i>
      </div>

      <audio ref={audioRef} style={{ display: 'none' }} loop>
        <source
          src={process.env.REACT_APP_VISION_AI_STREAM_AUDIO_PATH}
          type="audio/mpeg"
        />
        {t('audioNotSupported')}
      </audio>

      <div
        className={`custom-file-upload-container ${
          transcribe ? 'single-button' : ''
        }`}
      >
        {/* Botão para escolher imagem ou vídeo */}
        <label className="custom-file-upload">
        <input
            type="file"
            onChange={handleMediaChange}
            accept="video/mp4, video/quicktime, video/x-msvideo, video/x-matroska, video/webm, video/ogg, image/*"
            style={{ display: 'none' }}
          />
          <img
            src="/buttons/upload.jpg"
            alt={t('uploadFile')}
            className="upload-btn-image"
          />
        </label>

        {/* Botão para escolher legenda (apenas se não for transcribe) */}
        {!transcribe && (
          <label className="custom-file-upload">
            <input
              type="file"
              onChange={handleSubtitleChange}
              accept=".srt"
              style={{ display: 'none' }}
            />
            <img
              src="/buttons/subtitle.jpg"
              alt={t('uploadSubtitle')}
              className="upload-btn-image"
            />
          </label>
        )}

        <div className="toggle-switch-container">
          {/* Transcribe */}
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="transcribe-video"
              checked={transcribe}
              onChange={toggleTranscribe}
            />
            <label htmlFor="transcribe-video" className="toggle-label transcribe-button">
              {t('transcribe')}
            </label>
          </div>

          {/* Embed */}
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="embed-video"
              checked={embed}
              onChange={toggleEmbed}
            />
            <label htmlFor="embed-video" className="toggle-label embed-button">
              {t('embed')}
            </label>
          </div>

          {/* Dubbing (desabilitado p/ Free e Starter) */}
          {transcribe && (
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="dubbing-video"
                checked={dubbing}
                onChange={toggleDubbing}
                disabled={disableDubbing}
                style={{
                  cursor: disableDubbing ? 'not-allowed' : 'pointer',
                  opacity: disableDubbing ? 0.5 : 1,
                }}
              />
              <label
                htmlFor="dubbing-video"
                className="toggle-label dubbing-button"
                style={{
                  cursor: disableDubbing ? 'not-allowed' : 'pointer',
                  opacity: disableDubbing ? 0.5 : 1,
                }}
              >
                {t('dubbing')}
              </label>
            </div>
          )}

          {/* Join (desabilitado p/ Free) */}
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="join-video"
              checked={joinVideo}
              onChange={toggleJoinVideo}
              disabled={disableJoinVideo}
              style={{
                cursor: disableJoinVideo ? 'not-allowed' : 'pointer',
                opacity: disableJoinVideo ? 0.5 : 1,
              }}
            />
            <label
              htmlFor="join-video"
              className="toggle-label join-button"
              style={{
                cursor: disableJoinVideo ? 'not-allowed' : 'pointer',
                opacity: disableJoinVideo ? 0.5 : 1,
              }}
            >
              {t('join')}
            </label>
          </div>

          {/* Cut (desabilitado p/ Free) */}
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="cut-video"
              checked={cutVideo}
              onChange={toggleCutVideo}
              disabled={disableCutVideo}
              style={{
                cursor: disableCutVideo ? 'not-allowed' : 'pointer',
                opacity: disableCutVideo ? 0.5 : 1,
              }}
            />
            <label
              htmlFor="cut-video"
              className="toggle-label cut-button"
              style={{
                cursor: disableCutVideo ? 'not-allowed' : 'pointer',
                opacity: disableCutVideo ? 0.5 : 1,
              }}
            >
              {t('cut')}
            </label>
          </div>
        </div>

        {/* Sub-flags de Dubbing */}
        {dubbing && (
          <div className="toggle-switch-container">
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="voice-clone"
                checked={syncVoiceClone}
                onChange={toggleSyncVoiceClone}
                disabled={disableSubFlagCloneVoice}
                style={{
                  cursor: disableSubFlagCloneVoice ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagCloneVoice ? 0.5 : 1,
                }}
              />
              <label
                htmlFor="voice-clone"
                className="toggle-label voice-clone-button"
                style={{
                  cursor: disableSubFlagCloneVoice ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagCloneVoice ? 0.5 : 1,
                }}
              >
                {t('voiceClone')}
              </label>
            </div>

            <div className="toggle-switch">
              <input
                type="checkbox"
                id="lip-movement"
                checked={syncLipMovement}
                onChange={toggleSyncLipMovement}
                disabled={disableSubFlagLipMovement}
                style={{
                  cursor: disableSubFlagLipMovement ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagLipMovement ? 0.5 : 1,
                }}
              />
              <label
                htmlFor="lip-movement"
                className="toggle-label lip-movement-button"
                style={{
                  cursor: disableSubFlagLipMovement ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagLipMovement ? 0.5 : 1,
                }}
              >
                {t('lipMovement')}
              </label>
            </div>

            <div className="toggle-switch">
              <input
                type="checkbox"
                id="lip-enhance"
                checked={syncLipEnhance}
                onChange={toggleSyncLipEnhance}
                disabled={disableSubFlagLipEnhance}
                style={{
                  cursor: disableSubFlagLipEnhance ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagLipEnhance ? 0.5 : 1,
                }}
              />
              <label
                htmlFor="lip-enhance"
                className="toggle-label lip-enhance-button"
                style={{
                  cursor: disableSubFlagLipEnhance ? 'not-allowed' : 'pointer',
                  opacity: disableSubFlagLipEnhance ? 0.5 : 1,
                }}
              >
                {t('lipEnhance')}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Área de vídeo e sliders para recorte, se ativo */}
      {mediaURL && (
        <div>
          {mediaType === 'video' ? (
            <>
              <video ref={videoRef} src={mediaURL} controls className="video-standard" />
              {cutVideo && (
                <div className="slider-container">
                  <div>
                    <label>
                      {t('startTime')}: {startTime.toFixed(2)}s
                    </label>
                    <Slider
                      min={0}
                      max={duration}
                      value={startTime}
                      onChange={(value) => setStartTime(value)}
                    />
                  </div>
                  <div>
                    <label>
                      {t('endTime')}: {endTime.toFixed(2)}s
                    </label>
                    <Slider
                      min={0}
                      max={duration}
                      value={endTime}
                      onChange={(value) => setEndTime(value)}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <img src={mediaURL} alt="Preview" className="image-preview" />
          )}
        </div>
      )}

      {/* Se for 'cutVideo', mostra botão de 'Cortar' */}
      {mediaURL && cutVideo && (
        <>
          <button
            className="custom-button"
            onClick={handleCutVideoClick}
            disabled={disableCutVideo}
            style={{
              cursor: disableCutVideo ? 'not-allowed' : 'pointer',
              opacity: disableCutVideo ? 0.5 : 1,
              marginTop: '10px',
            }}
          >
            {t('cutOriginalVideo')}
          </button>

          {cutProgress > 0 && !cutSuccess && (
            <Progress progress={cutProgress} className="video-progress-bar" />
          )}
          {cutSuccess && (
            <div style={{ color: 'green' }}>{t('cutSuccess')}</div>
          )}

          {cutSuccess && (
            <>
              <button
                className="custom-button"
                onClick={() => downloadFile(videoFile, 'cut_video.webm')}
              >
                {t('downloadCutVideo')}
              </button>
              {cutSubtitleFile && (
                <button
                  className="custom-button"
                  onClick={() => downloadFile(cutSubtitleFile, 'cut_subtitle.srt')}
                >
                  {t('downloadCutSubtitle')}
                </button>
              )}
            </>
          )}
        </>
      )}

      {/* Campo de diálogo agora SEM a restrição de transcribe */}
      {!cutVideo && (
        <>
          <div
            style={{
              marginTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ width: '420px' }}>
              <div className="language-section">
                {/* Se transcribe=true, exibimos InputLanguage + OutputLanguage */}
                {transcribe && (
                  <>
                    {/* Input Language */}
                    <div style={{ width: '320px', marginBottom: '10px' }}>
                      <LanguageSelector
                        selectedLanguage={inputLanguage}
                        onLanguageChange={setInputLanguage}
                        label={
                          <span
                            style={{
                              color: '#fff',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              display: 'block',
                            }}
                          >
                            {t('inputLanguage')}
                          </span>
                        }
                        options={languages}
                      />
                    </div>

                    {/* Output Language + toggle de tradução de página */}
                    <div
                      style={{
                        width: '420px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ width: '320px' }}>
                        <LanguageSelector
                          selectedLanguage={outputLanguage}
                          onLanguageChange={setOutputLanguage}
                          label={
                            <span
                              style={{
                                color: '#fff',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                display: 'block',
                              }}
                            >
                              {t('outputLanguage')}
                            </span>
                          }
                          options={languages}
                        />
                      </div>

                      <div className="toggle-switch">
                        <input
                          type="checkbox"
                          id="flag-page"
                          checked={translatePage}
                          onChange={() => setTranslatePage(!translatePage)}
                        />
                        <label htmlFor="flag-page" className="toggle-label page-button">
                          {t('page')}
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {!transcribe && (
                  <>
                    {/* Apenas Output Language + toggle da página */}
                    <div
                      style={{
                        width: '420px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ width: '320px' }}>
                        <LanguageSelector
                          selectedLanguage={outputLanguage}
                          onLanguageChange={setOutputLanguage}
                          label={
                            <span
                              style={{
                                color: '#fff',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                display: 'block',
                              }}
                            >
                              {t('outputLanguage')}
                            </span>
                          }
                          options={languages}
                        />
                      </div>

                      <div className="toggle-switch">
                        <input
                          type="checkbox"
                          id="flag-page"
                          checked={translatePage}
                          onChange={() => setTranslatePage(!translatePage)}
                        />
                        <label htmlFor="flag-page" className="toggle-label page-button">
                          {t('page')}
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* Transition Ratio */}
                <div style={{ width: '320px' }}>
                  <label
                    htmlFor="transitionRatio"
                    style={{
                      marginBottom: '0.5rem',
                      color: '#fff',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      display: 'block',
                    }}
                  >
                    {t('transitionRatioLabel')}
                  </label>
                  <div
                    className="tooltip"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="number"
                      id="transitionRatio"
                      name="transitionRatio"
                      step="0.1"
                      min="0.1"
                      max="9.9"
                      value={transitionRatio}
                      onChange={(e) => handleTransitionRatioChange(e.target.value)}
                    />
                    <div className="tooltip-text">
                      {mediaType === 'image'
                        ? t('transitionRatioImageTooltip').replace('{seconds}', Math.round(transitionRatio))
                        : t('transitionRatioTooltip')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <input
            type="text"
            value={dialogue}
            onChange={handleDialogueChange}
            placeholder={
              mediaType === 'image'
                ? t('imagePromptPlaceholder')
                : t('dialoguePlaceholder')
            }
            style={{ marginTop: '10px' }}
          />

        </>
      )}

      {/* Botão principal de processar */}
      <button
        className="custom-button"
        onClick={handleProcessVideoClick}
        style={{ marginTop: '20px' }}
      >
        {transcribe
          ? t('processVideo')
          : dialogue
          ? t('cutVideoByDialogue')
          : t('cutVideoBySubtitle')}
      </button>

      {uploadProgress > 0 && (
        <Progress progress={uploadProgress} className="video-progress-bar" />
      )}
      {uploadSuccess && (
        <div style={{ color: 'green' }}>{t('uploadSuccess')}</div>
      )}
      {error && (
        <div style={{ color: 'red' }}>{error}</div>
      )}

      {/* Bloco de anúncio, mostrado se showAd=true */}
      {showAd && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {isSimulation ? (
            <div
              style={{
                padding: '20px',
                border: '1px solid #ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              <p>{s('adSimulation')}</p>
            </div>
          ) : (
            <>
              <script
                async
                src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6453889750420507"
                crossOrigin="anonymous"
              ></script>
              <ins
                className="adsbygoogle"
                style={{
                  display: 'inline-block',
                  width: '336px',
                  height: '280px',
                  backgroundColor: '#fff', // para evitar tela preta
                  border: '1px solid #ccc',
                }}
                data-ad-client="ca-pub-6453889750420507"
                data-ad-slot="1234567890"
              ></ins>
              <script>{`
                (adsbygoogle = window.adsbygoogle || []).push({});
              `}</script>
            </>
          )}
        </div>
      )}

      <audio
        ref={audioRef}
        src={process.env.REACT_APP_VISION_AI_STREAM_AUDIO_PATH}
      />

      {/* Lista de vídeos processados */}
      {processedVideos.length > 0 && (
        <>
          <div className="video-thumbnail-container">
            {processedVideos.map((video, index) => (
              <div
                key={video.id}
                className="video-thumbnail"
                draggable
                onDragStart={(event) => handleDragStart(event, index)}
                onDrop={(event) => handleDrop(event, index)}
                onDragOver={handleDragOver}
              >
                <video controls>
                  <source
                    src={`${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api${video.url}`}
                    type="video/mp4"
                  />
                </video>
                <div
                  className="delete-icon"
                  onClick={() => handleDelete(video.id)}
                >
                  🗑️
                </div>
              </div>
            ))}
          </div>

          {/* Botão de juntar vídeos */}
          <button
            className="custom-button"
            onClick={handleJoinVideosClick}
            disabled={disableJoinVideo}
            style={{
              cursor: disableJoinVideo ? 'not-allowed' : 'pointer',
              opacity: disableJoinVideo ? 0.5 : 1,
            }}
          >
            {t('joinVideos')}
          </button>

          {joinProgress > 0 && !joinSuccess && (
            <Progress progress={joinProgress} className="video-progress-bar" />
          )}
          {joinSuccess && (
            <div style={{ color: 'green' }}>{t('joinSuccess')}</div>
          )}

          {processedVideos.length < 2 && !joinSuccess && (
            <div style={{ color: 'red' }}>{t('joinError')}</div>
          )}
        </>
      )}

      {/* Botão de logout (com anúncio se Free) */}
      <button
        className="custom-button"
        onClick={handleLogoutClick}
        style={{ marginTop: '20px' }}
      >
        {t('logout')}
      </button>
    </div>
  );
}

export default VideoEditor;
